import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Application, Graphics, Text } from 'pixi.js';

interface ScatterPoint {
  x: number;
  y: number;
  label: string;
}

interface ScatterFrame {
  timestamp: string;
  points: ScatterPoint[];
}

interface AnimatedScatterPlotProps {
  data: ScatterFrame[];
  animationSpeed: number;
  shouldDraw: boolean;
  isRecording: boolean;
  stopRecording: () => void;
  backgroundColor: string;
  showWatermark: boolean;
  xAxisLabel: string;
  yAxisLabel: string;
  labelFontSize?: number;
  onAnimationComplete?: () => void;
}

const AnimatedScatterPlot: React.FC<AnimatedScatterPlotProps> = ({
  data,
  animationSpeed,
  shouldDraw,
  isRecording,
  stopRecording,
  backgroundColor,
  showWatermark,
  xAxisLabel,
  yAxisLabel,
  labelFontSize = 14,
  onAnimationComplete
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<Application | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isAppReady, setIsAppReady] = useState(false);
  const currentFrameIndexRef = useRef(0);
  const isAnimatingRef = useRef(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);

  // Initialize PixiJS application
  useEffect(() => {
    if (!containerRef.current) return;

    const initApp = async () => {
      try {
        const app = new Application();
        const containerWidth = containerRef.current!.clientWidth || 800;
        const containerHeight = containerRef.current!.clientHeight || 600;
        
        await app.init({
          width: containerWidth,
          height: containerHeight,
          backgroundColor: 0xffffff,
          antialias: true,
          resolution: window.devicePixelRatio || 1,
          autoDensity: true,
        });

        if (containerRef.current && app.canvas) {
          app.canvas.style.width = '100%';
          app.canvas.style.height = '100%';
          app.canvas.style.display = 'block';
          app.canvas.style.position = 'absolute';
          app.canvas.style.top = '0';
          app.canvas.style.left = '0';
          containerRef.current.appendChild(app.canvas);
          appRef.current = app;
          setIsAppReady(true);
        }
      } catch (error) {
        console.error('Failed to initialize PixiJS app:', error);
      }
    };

    initApp();

    return () => {
      if (appRef.current) {
        appRef.current.destroy(true);
        appRef.current = null;
      }
    };
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (appRef.current && containerRef.current) {
        const containerWidth = containerRef.current.clientWidth || 800;
        const containerHeight = containerRef.current.clientHeight || 600;
        appRef.current.renderer.resize(containerWidth, containerHeight);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const drawScatterPlot = useCallback((frame: ScatterFrame, alpha: number = 1) => {
    if (!appRef.current) return;

    const app = appRef.current;
    const stage = app.stage;
    stage.removeChildren();

    // Set background
    const bgColor = backgroundColor === 'black' ? 0x000000 : 
                   backgroundColor === '#404040' ? 0x404040 : 0xffffff;
    
    const background = new Graphics()
      .beginFill(bgColor)
      .drawRect(0, 0, app.renderer.width, app.renderer.height)
      .endFill();
    stage.addChild(background);

    if (!frame.points || frame.points.length === 0) return;

    // Calculate bounds
    const xValues = frame.points.map(p => p.x);
    const yValues = frame.points.map(p => p.y);
    const minX = Math.min(...xValues);
    const maxX = Math.max(...xValues);
    const minY = Math.min(...yValues);
    const maxY = Math.max(...yValues);

    // Add padding to bounds
    const xPadding = (maxX - minX) * 0.1;
    const yPadding = (maxY - minY) * 0.1;
    const paddedMinX = minX - xPadding;
    const paddedMaxX = maxX + xPadding;
    const paddedMinY = minY - yPadding;
    const paddedMaxY = maxY + yPadding;

    // Calculate scale factors
    const margin = 80;
    const plotWidth = app.renderer.width - 2 * margin;
    const plotHeight = app.renderer.height - 2 * margin;
    const scaleX = plotWidth / (paddedMaxX - paddedMinX);
    const scaleY = plotHeight / (paddedMaxY - paddedMinY);

    // Draw axes
    const axesColor = bgColor === 0x000000 ? 0xffffff : 0x000000;
    const axes = new Graphics()
      .setStrokeStyle({ width: 2, color: axesColor, alpha })
      .moveTo(margin, margin)
      .lineTo(margin, app.renderer.height - margin)
      .moveTo(margin, app.renderer.height - margin)
      .lineTo(app.renderer.width - margin, app.renderer.height - margin);
    stage.addChild(axes);

    // Draw axis labels
    const labelStyle = {
      fontFamily: 'Arial',
      fontSize: labelFontSize,
      fill: axesColor
    };

    // X-axis label
    const xAxisLabelText = new Text(xAxisLabel || 'X Values', labelStyle);
    xAxisLabelText.x = app.renderer.width / 2;
    xAxisLabelText.y = app.renderer.height - 20;
    xAxisLabelText.anchor.set(0.5, 0.5);
    xAxisLabelText.alpha = alpha;
    stage.addChild(xAxisLabelText);

    // Y-axis label
    const yAxisLabelText = new Text(yAxisLabel || 'Y Values', labelStyle);
    yAxisLabelText.x = 20;
    yAxisLabelText.y = app.renderer.height / 2;
    yAxisLabelText.anchor.set(0.5, 0.5);
    yAxisLabelText.rotation = -Math.PI / 2;
    yAxisLabelText.alpha = alpha;
    stage.addChild(yAxisLabelText);

    // Draw axis tick marks and labels
    const tickStyle = {
      fontFamily: 'Arial',
      fontSize: Math.max(8, labelFontSize - 2),
      fill: axesColor
    };

    // X-axis tick marks and labels
    const numXTicks = 5;
    for (let i = 0; i <= numXTicks; i++) {
      const tickX = margin + (i / numXTicks) * plotWidth;
      const tickValue = paddedMinX + (i / numXTicks) * (paddedMaxX - paddedMinX);
      
      // Draw tick mark
      const tickMark = new Graphics()
        .setStrokeStyle({ width: 1, color: axesColor, alpha })
        .moveTo(tickX, app.renderer.height - margin)
        .lineTo(tickX, app.renderer.height - margin + 5);
      stage.addChild(tickMark);
      
      // Draw tick label
      const tickLabel = new Text(tickValue.toFixed(1), tickStyle);
      tickLabel.x = tickX;
      tickLabel.y = app.renderer.height - margin + 20;
      tickLabel.anchor.set(0.5, 0.5);
      tickLabel.alpha = alpha;
      stage.addChild(tickLabel);
    }

    // Y-axis tick marks and labels
    const numYTicks = 5;
    for (let i = 0; i <= numYTicks; i++) {
      const tickY = app.renderer.height - margin - (i / numYTicks) * plotHeight;
      const tickValue = paddedMinY + (i / numYTicks) * (paddedMaxY - paddedMinY);
      
      // Draw tick mark
      const tickMark = new Graphics()
        .setStrokeStyle({ width: 1, color: axesColor, alpha })
        .moveTo(margin, tickY)
        .lineTo(margin - 5, tickY);
      stage.addChild(tickMark);
      
      // Draw tick label
      const tickLabel = new Text(tickValue.toFixed(1), tickStyle);
      tickLabel.x = margin - 10;
      tickLabel.y = tickY;
      tickLabel.anchor.set(1, 0.5);
      tickLabel.alpha = alpha;
      stage.addChild(tickLabel);
    }

    // Draw grid lines
    const gridColor = bgColor === 0x000000 ? 0x333333 : 0xcccccc;
    const grid = new Graphics()
      .setStrokeStyle({ width: 1, color: gridColor, alpha: 0.5 });

    // Vertical grid lines
    const numGridLines = 5;
    for (let i = 0; i <= numGridLines; i++) {
      const x = margin + (i / numGridLines) * plotWidth;
      grid.moveTo(x, margin).lineTo(x, app.renderer.height - margin);
    }
    // Horizontal grid lines
    for (let i = 0; i <= numGridLines; i++) {
      const y = margin + (i / numGridLines) * plotHeight;
      grid.moveTo(margin, y).lineTo(app.renderer.width - margin, y);
    }
    stage.addChild(grid);

    // Draw scatter points
    const colors = [0xff6b6b, 0x4ecdc4, 0x45b7d1, 0x96ceb4, 0xfeca57, 0xff9ff3, 0x54a0ff, 0x5f27cd];
    
    frame.points.forEach((point, index) => {
      const screenX = margin + (point.x - paddedMinX) * scaleX;
      const screenY = app.renderer.height - margin - (point.y - paddedMinY) * scaleY;

      // Draw point
      const pointColor = colors[index % colors.length];
      const scatterPoint = new Graphics()
        .beginFill(pointColor, alpha)
        .drawCircle(0, 0, 6)
        .endFill();
      scatterPoint.x = screenX;
      scatterPoint.y = screenY;
      stage.addChild(scatterPoint);

      // Draw point label
      const pointLabel = new Text(point.label, {
        fontFamily: 'Arial',
        fontSize: Math.max(8, labelFontSize - 2),
        fill: axesColor
      });
      pointLabel.x = screenX + 10;
      pointLabel.y = screenY - 10;
      pointLabel.anchor.set(0, 0.5);
      pointLabel.alpha = alpha;
      stage.addChild(pointLabel);
    });

    // Draw timestamp
    const timestampText = new Text(frame.timestamp, {
      fontFamily: 'Arial',
      fontSize: Math.round(labelFontSize * 1.3),
      fill: axesColor
    });
    timestampText.x = app.renderer.width / 2;
    timestampText.y = 30;
    timestampText.anchor.set(0.5, 0.5);
    timestampText.alpha = alpha;
    stage.addChild(timestampText);

    // Draw watermark
    if (showWatermark && localStorage.getItem('watermarkRemoved') !== 'true') {
      const isDarkBackground = backgroundColor === 'black' || backgroundColor === '#404040';
      const watermarkColor = isDarkBackground ? 0xffffff : 0x000000;
      const watermark = new Text('graffy.ca', {
        fontFamily: 'Arial',
        fontSize: 48,
        fill: watermarkColor
      });
      watermark.x = app.renderer.width / 2;
      watermark.y = app.renderer.height / 2;
      watermark.anchor.set(0.5, 0.5);
      watermark.alpha = 0.1;
      stage.addChild(watermark);
    }

    app.render();
  }, [backgroundColor, showWatermark, xAxisLabel, yAxisLabel, labelFontSize]);

  const animate = useCallback(() => {
    try {
      if (!shouldDraw || data.length === 0 || !isAppReady) {
        setIsAnimating(false);
        isAnimatingRef.current = false;
        return;
      }

      if (currentFrameIndexRef.current >= data.length) {
        setIsAnimating(false);
        isAnimatingRef.current = false;
        if (isRecording) {
          stopRecording();
        }
        if (onAnimationComplete) {
          onAnimationComplete();
        }
        return;
      }

      const frame = data[currentFrameIndexRef.current];
      drawScatterPlot(frame, 1);

      setTimeout(() => {
        currentFrameIndexRef.current++;
        if (shouldDraw && isAnimatingRef.current) {
          animate();
        }
      }, 1000 / animationSpeed);
    } catch (err) {
      console.error('Animation error:', err);
      setIsAnimating(false);
      isAnimatingRef.current = false;
    }
  }, [shouldDraw, data.length, isAppReady, animationSpeed, drawScatterPlot, isRecording, stopRecording, onAnimationComplete]);

  useEffect(() => {
    if (shouldDraw && data.length > 0 && isAppReady) {
      currentFrameIndexRef.current = 0;
      setIsAnimating(true);
      isAnimatingRef.current = true;
      animate();
    } else {
      setIsAnimating(false);
      isAnimatingRef.current = false;
    }
  }, [shouldDraw, data.length, isAppReady, animate]);

  const startRecording = useCallback(() => {
    if (!appRef.current || !appRef.current.canvas) {
      console.error('Canvas not available for recording');
      return;
    }

    const mimeTypes = [
      'video/mp4;codecs=avc1.42E01E',
      'video/mp4;codecs=avc1.4D401E',
      'video/mp4;codecs=avc1.64001E',
      'video/mp4;codecs=h264',
      'video/mp4',
      'video/webm;codecs=h264',
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/webm'
    ];

    let selectedMimeType = '';
    for (const mimeType of mimeTypes) {
      if (MediaRecorder.isTypeSupported(mimeType)) {
        selectedMimeType = mimeType;
        break;
      }
    }

    if (!selectedMimeType) {
      console.error('No supported video MIME type found');
      return;
    }

    try {
      const stream = appRef.current.canvas.captureStream(30);
      const mediaRecorder = new MediaRecorder(stream, { mimeType: selectedMimeType });
      
      recordedChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: selectedMimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `animated-scatter-plot.${selectedMimeType.includes('mp4') ? 'mp4' : 'webm'}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
    } catch (error) {
      console.error('Failed to start recording:', error);
    }
  }, []);

  const stopRecordingInternal = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  useEffect(() => {
    if (isRecording) {
      startRecording();
    } else {
      stopRecordingInternal();
    }
  }, [isRecording, startRecording, stopRecordingInternal]);

  return (
    <div 
      ref={containerRef} 
      style={{ 
        width: '100%', 
        height: '600px', 
        position: 'relative',
        border: '1px solid #ccc',
        borderRadius: '8px',
        overflow: 'hidden'
      }}
    />
  );
};

export default AnimatedScatterPlot; 